import classNames from 'classnames';
import {
  BusinessIDType,
  EntityType,
  PartyResponse,
  PartyVerificationStatus,
  UpdatePartyBody,
} from 'compliance-api/generated';
import { FormikProvider, useFormik, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { PartyContactsTab } from '@/modules/admin-dashboard/components/contractManagement/FullInfoModal/PartyContactsTab.tsx';
import { PartyContractsTab } from '@/modules/admin-dashboard/components/contractManagement/FullInfoModal/PartyContractsTab.tsx';
import {
  PartyDetailsFormValues,
  PartyDetailsTab,
} from '@/modules/admin-dashboard/components/contractManagement/FullInfoModal/PartyDetailsTab.tsx';
import { PartyNotesTab } from '@/modules/admin-dashboard/components/contractManagement/FullInfoModal/PartyNotesTab.tsx';
import {
  usePartyQuery,
  useUpdatePartyMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import {
  Badge,
  BaseModalProps,
  Button,
  ModalContentWrapper,
  PopoverMenu,
  Spinner,
  TabButtons,
  TabItem,
  Tabs,
} from 'ui';
import { usePrompt } from 'ui/hooks';
import { ChevronDownIcon } from 'ui/icons';

interface PartyFullInfoModalProps extends BaseModalProps {
  partyId: number;
  readOnly?: boolean;
}

type PartyInfoTab = 'details' | 'people' | 'contracts' | 'notes';

const PARTY_INFO_TABS: { name: PartyInfoTab; label: string }[] = [
  { name: 'details', label: 'Details' },
  { name: 'people', label: 'People' },
  { name: 'contracts', label: 'Contracts' },
  { name: 'notes', label: 'Notes' },
];

const PartyFullInfoModal = ({
  partyId,
  open,
  onClose,
  readOnly = false,
}: PartyFullInfoModalProps) => {
  const [activeTab, setActiveTab] = useState<PartyInfoTab>('details');
  const { data, isFetching } = usePartyQuery(partyId, !partyId);
  const party = data?.party;
  const contacts = useMemo(() => data?.contacts || [], [data?.contacts]);

  const partyForm = useFormik<PartyDetailsFormValues>({
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: getInitialValues(party),
    validationSchema: partyDetailsValidationSchema,
    onSubmit: async values => {
      if (!party) return;

      const payload: UpdatePartyBody = {
        legal_name: values.legal_name,
        trading_name: values.trading_name || undefined,
        business_number: values.business_number || undefined,
        business_number_type: values.business_number_type || undefined,
        entity_type: values.entity_type || undefined,
        incorporation_country: values.incorporation_country || undefined,
        phone: values.phone || undefined,
        address_line1: values.address_line1 || undefined,
        address_line2: values.address_line2 || undefined,
        city: values.city || undefined,
        state: values.state || undefined,
        postcode: values.postcode || undefined,
        is_active: values.is_active,
      };

      if (readOnly) return;

      await updatePartyMutation({ id: party.id, data: payload });
      partyForm.resetForm({ values });
    },
  });

  const { mutateAsync: updatePartyMutation, isLoading: isUpdating } = useUpdatePartyMutation(
    partyForm.setErrors,
  );

  useEffect(() => {
    if (!party) return;
    partyForm.resetForm({ values: getInitialValues(party) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party]);

  const hasUnsavedChanges = partyForm.dirty && !partyForm.isSubmitting;
  const leaveMessage =
    'Do you really want to leave this form? You will lose some entered information.';

  usePrompt(hasUnsavedChanges, leaveMessage);

  const handleClose = useCallback(() => {
    if (readOnly) {
      onClose();
      return;
    }

    if (hasUnsavedChanges && !window.confirm(leaveMessage)) {
      return;
    }

    onClose();
  }, [hasUnsavedChanges, leaveMessage, onClose, readOnly]);

  const handleVerify = useCallback(async () => {
    if (!party) return;

    if (readOnly) return;

    await updatePartyMutation({
      id: party.id,
      data: {
        verification_status: PartyVerificationStatus.Verified,
      },
    });
  }, [party, readOnly, updatePartyMutation]);

  const tabs = PARTY_INFO_TABS.map(({ name, label }) => ({
    name,
    label,
    container:
      name === 'details' && party ? (
        <PartyDetailsTab showSaveButton={activeTab === 'details'} readOnly={readOnly} />
      ) : name === 'people' ? (
        <PartyContactsTab partyId={partyId} contacts={contacts} readOnly={readOnly} />
      ) : name === 'contracts' ? (
        <PartyContractsTab partyId={partyId} />
      ) : name === 'notes' && party ? (
        <PartyNotesTab party={party} partyId={partyId} readOnly={readOnly} />
      ) : (
        <div />
      ),
  })) as TabItem<PartyInfoTab>[];

  return (
    <ModalContentWrapper
      title={party?.legal_name || 'Party details'}
      variant="right"
      open={open}
      onClose={handleClose}
      classes={{
        children: 'p-0 pb-0',
        dialogPanel: '!max-w-[500px]',
        titleWrapper: 'flex-1 min-w-0 pr-3',
        title: '!text-xl !leading-7',
      }}
    >
      {isFetching && !party ? (
        <div className="flex min-h-[320px] items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      ) : !party ? (
        <div className="px-5 py-8 text-sm text-gray-500">Party not found.</div>
      ) : (
        <FormikProvider value={partyForm}>
          <div>
            <div className="px-5 pb-4 pt-4">
              <div className="flex flex-wrap items-center justify-start gap-3">
                <PartyStatusBadge isActive={partyForm.values.is_active} readOnly={readOnly} />
                {party.is_ai_captured && party.verification_status && (
                  <PartyVerificationBadge status={party.verification_status} />
                )}
                {party.is_ai_captured &&
                  party.verification_status === PartyVerificationStatus.New && (
                    <Button
                      className="h-8 min-h-[32px] w-fit min-w-0 px-3 py-1 text-xs"
                      onClick={() => void handleVerify()}
                      disabled={isUpdating || readOnly}
                      loading={isUpdating}
                    >
                      Verify
                    </Button>
                  )}
                {!readOnly && partyForm.dirty && activeTab !== 'details' && (
                  <Button
                    className="h-8 min-h-[32px] w-fit min-w-0 px-3 py-1 text-xs"
                    onClick={partyForm.submitForm}
                    disabled={isUpdating}
                    loading={isUpdating}
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>

            <TabButtons<PartyInfoTab>
              tabs={tabs}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              className="border-b border-gray-200 px-5 [&_.rounded-underline:after]:bottom-[-3px] [&>div]:grid [&>div]:w-full [&>div]:min-w-0 [&>div]:grid-cols-4 [&>div]:gap-0 [&_button]:h-12 [&_button]:justify-center"
            />

            <div
              className={classNames(
                activeTab === 'people' || activeTab === 'contracts' ? 'px-5 py-4' : 'px-5 py-8',
                activeTab === 'notes' && 'px-5 py-4',
              )}
            >
              <Tabs items={tabs} activeTab={activeTab} />
            </div>
          </div>
        </FormikProvider>
      )}
    </ModalContentWrapper>
  );
};

const PartyStatusBadge = ({ isActive, readOnly }: { isActive: boolean; readOnly: boolean }) => {
  const { setFieldValue } = useFormikContext<PartyDetailsFormValues>();
  if (readOnly) {
    return (
      <div
        className={classNames(
          'inline-flex h-8 min-w-[130px] items-center justify-between gap-2 rounded px-3 text-sm leading-[22px]',
          isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700',
        )}
      >
        <span>{isActive ? 'Active' : 'Inactive'}</span>
      </div>
    );
  }

  const items = [
    {
      label: 'Active',
      onClick: () => {
        void setFieldValue('is_active', true);
      },
      className: classNames({
        'text-green-700': isActive,
        'text-gray-900': !isActive,
      }),
    },
    {
      label: 'Inactive',
      onClick: () => {
        void setFieldValue('is_active', false);
      },
      className: classNames({
        'text-red-700': !isActive,
        'text-gray-900': isActive,
      }),
    },
  ];

  return (
    <PopoverMenu
      button={
        <div
          className={classNames(
            'inline-flex h-8 min-w-[130px] items-center justify-between gap-2 rounded px-3 text-sm leading-[22px]',
            isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700',
          )}
        >
          <span>{isActive ? 'Active' : 'Inactive'}</span>
          <img src={ChevronDownIcon} alt="" className="h-4 w-4 shrink-0" />
        </div>
      }
      items={items}
      placement="bottom-start"
      classes={{
        container: 'w-fit',
        panel:
          'w-[160px] p-0 bg-white border border-gray-200 rounded shadow-lg overflow-hidden z-50',
      }}
    />
  );
};

const PartyVerificationBadge = ({ status }: { status: PartyVerificationStatus | undefined }) => {
  if (!status) return null;

  return (
    <Badge
      className={classNames(
        'h-8 rounded px-3 text-sm',
        status === PartyVerificationStatus.Verified
          ? 'bg-green-50 text-green-700'
          : 'bg-orange-50 text-orange-700',
      )}
    >
      {status === PartyVerificationStatus.Verified ? 'Verified' : 'New'}
    </Badge>
  );
};

const getInitialValues = (party?: PartyResponse): PartyDetailsFormValues => ({
  legal_name: party?.legal_name || '',
  trading_name: party?.trading_name || '',
  business_number: party?.business_number || '',
  business_number_type: party?.business_number_type || BusinessIDType.Abn,
  entity_type: party?.entity_type || null,
  incorporation_country: party?.incorporation_country || '',
  phone: party?.phone || '',
  address_line1: party?.address_line1 || '',
  address_line2: party?.address_line2 || '',
  city: party?.city || '',
  state: party?.state || '',
  postcode: party?.postcode || '',
  is_active: party?.is_active ?? true,
});

const partyDetailsValidationSchema = yup.object({
  legal_name: yup.string().trim().required('Legal name is required').max(255),
  trading_name: yup.string().trim().max(255),
  business_number: yup.string().trim().max(64),
  business_number_type: yup.mixed<BusinessIDType>().nullable(),
  entity_type: yup.mixed<EntityType>().nullable(),
  incorporation_country: yup.string().trim().max(2),
  phone: yup.string().trim().max(50),
  address_line1: yup.string().trim().max(255),
  address_line2: yup.string().trim().max(255),
  city: yup.string().trim().max(120),
  state: yup.string().trim().max(120),
  postcode: yup.string().trim().max(20),
  is_active: yup.boolean().required(),
});

export default PartyFullInfoModal;
